<template>
  <b-modal
    v-model="editorShown"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">{{ formTitle }}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity: 1 !important"
          @click="$emit('onHidden')"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div v-show="errorMessage" class="text-left text-danger mt-1 mr-auto">
        {{ errorMessage }}
      </div>
      <div class="text-right mt-1 mr-2">
        <b-button @click="$emit('onHidden')" variant="secondary">
          Cancel
        </b-button>
        <b-button
          v-if="!belongsToRecurrence"
          class="ml-2"
          @click="submitScheduleEvent"
          variant="primary"
        >
          Save
        </b-button>
        <b-button
          v-if="belongsToRecurrence"
          class="ml-2"
          @click="showRecurringEventModal"
          variant="primary"
        >
          Save
        </b-button>
      </div>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Status*"
          :state="formErrors.status.length == 0 ? null : false"
          :invalid-feedback="formErrors.status.join(', ')"
        >
          <b-form-select
            v-model="form.status"
            :style="{
              color: statusLabelColor,
              backgroundColor: statusBackgroundColor,
              fontWeight: 'bold'
            }"
          >
            <b-form-select-option
              v-for="status in statuses"
              :key="status.value"
              :value="status.value"
              :style="{
                color: status.data.color,
                backgroundColor: 'white',
                fontWeight: 'bold'
              }"
            >
              {{ status.text }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        <b-form-group
          label="Patient (Active & Pending only)*"
          :state="formErrors.eoc.length == 0 ? null : false"
          :invalid-feedback="formErrors.eoc.join(', ')"
        >
          <eoc-picker
            :eocs="episodesOfCare"
            v-model="selectedEoc"
            :async="true"
            @search="
              ({ search, loading }) => fetchEocsOptionsPicker(search, loading)
            "
          />
        </b-form-group>
        <b-form-group
          :label="this.requireProviderInEvents ? 'Provider*' : 'Provider'"
          :state="formErrors.user.length == 0 ? null : false"
          :invalid-feedback="formErrors.user.join(', ')"
        >
          <v-select :options="providerUsers" v-model="form.user" />
        </b-form-group>
        <b-form-group
          label="Date, time and duration"
          :state="formErrors.dateTimeDuration.length == 0 ? null : false"
          :invalid-feedback="formErrors.dateTimeDuration.join(', ')"
        >
          <date-time-duration-picker
            v-model="selectedDateTimeDuration"
            :dateDisabledFn="datesNonWorking"
          />
        </b-form-group>
        <b-form-group
          label="Place of service*"
          :state="formErrors.placeOfService.length == 0 ? null : false"
          :invalid-feedback="formErrors.placeOfService.join(', ')"
        >
          <div class="select-places-of-service">
            <div>
              <v-select
                :clearable="false"
                class=""
                :options="placesOfService"
                v-model="form.placeOfService"
              />
            </div>
          </div>
        </b-form-group>
        <b-form-group label="Room">
          <div class="select_rooms">
            <div>
              <v-select :options="rooms" v-model="form.room" />
            </div>
          </div>
        </b-form-group>
        <b-form-group v-if="isZoomEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
              :disabled="!form.user"
              @click="form.scheduleZoomMeeting = !form.scheduleZoomMeeting"
              :value="form.scheduleZoomMeeting"
              height="30"
              width="50"
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
              class="mr-2"
            />
            <span class="mr-2">Zoom Meeting</span>
            <img :src="require('../../assets/zoom_icon.png')" width="30" />
          </div>
          <div
            v-if="!form.user"
            class="text-danger mt-2"
            style="font-size: 15px"
          >
            Select Provider to attach a Zoom meeting.
          </div>
        </b-form-group>
        <b-form-group v-if="isTeamsEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
              @click="form.scheduleTeamsMeeting = !form.scheduleTeamsMeeting"
              :value="form.scheduleTeamsMeeting"
              height="30"
              width="50"
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
              class="mr-2"
            />
            <span class="mr-2">Teams Meeting</span>
            <img :src="require('../../assets/icons/teams.png')" width="30" />
          </div>
        </b-form-group>
        <b-form-group v-if="isGoogleMeetEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
              @click="form.scheduleGoogleMeetMeeting = !form.scheduleGoogleMeetMeeting"
              :value="form.scheduleGoogleMeetMeeting"
              :disabled="!form.user"
              height="30"
              width="50"
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
              class="mr-2"
            />
            <span class="mr-2">Google Meet Meeting</span>
            <img :src="require('../../assets/google_icon.png')" width="30" />
          </div>
          <div
              v-if="!form.user"
              class="text-danger mt-2"
              style="font-size: 15px"
          >
            Select Provider to attach a Google Meet meeting.
          </div>
        </b-form-group>
        <b-form-group class="mt-2">
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center">
              <vue-toggles
                @click="form.isRecurrent = !form.isRecurrent"
                :value="form.isRecurrent"
                height="30"
                width="50"
                checkedBg="#34C759"
                uncheckedBg="#FF6161"
                :disabled="eventIsInPast"
              />
              <span class="ml-2">Make event recurrent</span>
            </div>
            <div
              v-if="eventIsInPast && !form.isRecurrent"
              class="text-danger mt-2"
              style="font-size: 15px"
            >
              Recurrence is only available on scheduled events in the future.
            </div>
          </div>
        </b-form-group>
        <b-form-group v-show="form.isRecurrent" class="position-relative">
          <div
            class="position-absolute"
            style="
              inset: -63px 0px -16px -7px;
              background-color: #ccc;
              opacity: 0.5;
              z-index: 2;
            "
            v-if="eventIsInPast"
          ></div>
          <recurrence-control
            v-model="form.recurrence"
            :startDate="startDateTime"
            :formErrors="formErrors.recurrence"
            :maxOccurrences="300"
          />
          <div
            v-if="eventIsInPast && form.isRecurrent"
            class="text-danger"
            style="font-size: 15px; margin-bottom: 56px"
          >
            Recurrence is only available on scheduled events in the future.
          </div>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Services*"
          :state="formErrors.services.value < 0 ? true : false"
          :invalid-feedback="formErrors.services.join('. ')"
        >
          <multiple-select-unit-rate
            v-model="form.services"
            :options="services"
            :hide-units="
              !hasCurrentUserPermissionAccess('others', 'patientBillingInfo')
            "
            :hide-mods="
              !hasCurrentUserPermissionAccess('others', 'patientBillingInfo')
            "
            :no-cached="this.noCached"
          />
        </b-form-group>
        <b-form-group v-if="form.eoc">
          <patient-insurances
            :insurances="getPatientInsurancesActives"
            :eocInsurances="eocInsurances"
            :eocBillType="form.eoc?.data.patient.billType"
            @formPatientInsurancesData="addPatientInsurancesData"
            @costVariations="getServicesCostVariations"
            @setIsSelfPay="setIsSelfPay"
          ></patient-insurances>
        </b-form-group>
        <b-form-group label="Diagnosis codes">
          <multiple-select
            v-model="form.icdCodeIds"
            :options="icdCodes"
            :search="(search, loading) => fetchIcdCodeOptions(search, loading)"
            placeholder="Search for ICD Codes"
            :allowDuplicates="true"
            :onDemand="true"
            :disableCache="true"
            :disableFilter="true"
          />
        </b-form-group>
        <b-form-group label="Reason">
          <b-form-textarea
            placeholder=""
            v-model="form.reason"
            rows="4"
            max-rows="4"
            style="height: 119px"
            maxlength="150"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
    </b-row>

    <b-modal
      title="Edit recurring event"
      header-bg-variant="primary"
      header-text-variant="white"
      size="md"
      no-close-on-backdrop
      centered
      ref="form-modal"
      @ok="e => submitScheduleEvent(e, selectedOption)"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <div class="text-right mt-1 mr-2">
          <b-button @click="cancel()" variant="secondary">
            Cancel
          </b-button>
          <b-button class="ml-2" @click="ok()" variant="primary">
            Confirm
          </b-button>
        </div>
      </template>
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          :options="options"
          v-model="selectedOption"
          :aria-describedby="ariaDescribedby"
          name="some-radios"
          stacked
        ></b-form-radio-group>
      </b-form-group>
    </b-modal>
  </b-modal>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import scheduleEventServer from "../../server/scheduleEvent";
import roomServer from "../../server/scheduleEventRoom";
import eocServer from "../../server/eoc";
import userServer from "../../server/user";
import serviceServer from "../../server/service";
import icdCodeServer from "../../server/icdCode";
import locationServer from "../../server/location";
import diagnosisCodeServer from "../../server/diagnosisCode";
import {
  validateRequired,
  validateDate,
  validateNumberLessEqualThan,
  validateNumberPositive,
  validateNumberNotNegative
} from "../../helpers/validation";
import { lightenDarkenColor } from "../../helpers/general";
import EocPicker from "../controls/EocPicker";
import DateTimeDurationPicker from "../controls/DateTimeDurationPicker.vue";
import MultipleSelect from "../controls/MultipleSelect.vue";
import MultipleSelectUnitRate from "../controls/MultipleSelectUnitRate.vue";

import RecurrenceControl from "../controls/RecurrenceControl.vue";
import { formatDatePretty } from "../../../../Breeze.Frontend/src/helpers/date";
import PatientInsurances from "../../components/episode_of_care/PatientInsurances.vue";
export default {
  name: "AppointmentAddEditModal",
  components: {
    EocPicker,
    DateTimeDurationPicker,
    RecurrenceControl,
    MultipleSelect,
    MultipleSelectUnitRate,
    PatientInsurances
  },
  props: [
    "scheduleEventId",
    "scheduleEventDefaultData",
    "locationId",
    "providerId"
  ],
  data() {
    return {
      form: {
        participantId: null,
        eoc: null,
        status: null,
        user: null,
        dateTimeDuration: null,
        services: [],
        icdCodeIds: [],
        reason: "",
        scheduleZoomMeeting: false,
        zoomMeetingUrl: null,
        scheduleTeamsMeeting: false,
        teamsMeetingUrl: null,
        scheduleGoogleMeetMeeting: false,
        googleMeetMeetingUrl: null,
        isRecurrent: false,
        recurrence: {
          frequencyNumber: 1,
          frequencyUnit: "Days",
          monthlyRepeat: null,
          endType: "ByOccurrences",
          endDate: null,
          occurrences: 10,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false
        },
        placeOfService: null,
        room: null
      },
      formErrors: {
        eoc: [],
        status: [],
        user: [],
        reason: [],
        dateTimeDuration: [],
        services: [],
        recurrence: {
          ends: [],
          weekDay: []
        },
        placeOfService: []
      },
      episodesOfCare: [],
      users: [],
      services: [],
      icdCodes: [],
      eocDiagnosisCodes: [],
      prepopulatedIcdCodes: [],
      rooms: [],
      errorMessage: null,
      serviceCostVariations: [],
      noCached: false,
      servicesAll: [],
      defaultServicesAll: [],
      usersAll: [],
      location: null,
      options: [
        { value: "ModifySingleEvent", text: "This event" },
        {
          value: "ModifyEventAndRecurrence",
          text: "This and following events"
        },
        { value: "ModifyRecurrence", text: "All events" }
      ],
      selectedOption: "ModifySingleEvent",
      eocInsurances: null,
      isSelfPay: false,
      servicesEmpty: false,
      isLoading: true,
      lastTimeout: null
    };
  },
  async mounted() {
    this.fetchEocs();
    this.fetchRooms();
    await this.fetchUsers();
    await this.fetchServices();
    await this.fetchPlacesOfService();
    await this.fetchScheduleEventStatuses();
    this.location = this.locationId
      ? await locationServer.get(this.locationId)
      : null;

    const defaultStatus = this.statuses.find(s => s.data.code == "show");
    let currentUserProvider = this.users.find(
      user => user.value === this.currentUser.id
    )?.data;
    let form = { ...this.form };
    if (this.scheduleEventId) {
      const scheduleEvent = await scheduleEventServer.get(this.scheduleEventId);
      const eoc = await eocServer.get(
        scheduleEvent.episodesOfCare[0].episodeOfCare.id
      );
      this.eocInsurances = {
        id: scheduleEvent.episodesOfCare[0].episodeOfCare.id,
        insurancePrimary:
          scheduleEvent.episodesOfCare[0].primaryPatientInsurance ?? null,
        insuranceSecundary:
          scheduleEvent.episodesOfCare[0].secondaryPatientInsurance ?? null,
        insuranceTertiary:
          scheduleEvent.episodesOfCare[0].tertiaryPatientInsurance ?? null
      };
      const srv = this.splitServicesByBillingCodes(scheduleEvent);
      form = {
        eoc: scheduleEvent.episodesOfCare
          ? {
              value: scheduleEvent.episodesOfCare[0].episodeOfCare.id,
              label: this.formatEocObj(
                scheduleEvent.episodesOfCare[0].episodeOfCare
              ),
              data: eoc
            }
          : null,
        user:
          scheduleEvent.users && scheduleEvent.users.length
            ? {
                value: scheduleEvent.users[0].id,
                label: this.formatUser(scheduleEvent.users[0]),
                data: this.providerUsers.find(
                  user => user.value === scheduleEvent.users[0].id
                )?.data
              }
            : this.scheduleEventDefaultData?.providerId ?? null,
        status: scheduleEvent.episodesOfCare[0].scheduleEventStatus.id,
        dateTimeDuration: {
          date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
          time: scheduleEvent.allDay
            ? "00:00:00"
            : this.formatTimeOnlyMysql(scheduleEvent.startDateTime),
          duration: scheduleEvent.allDay
            ? null
            : Math.round(
                moment
                  .duration(
                    moment(scheduleEvent.endDateTime).diff(
                      moment(scheduleEvent.startDateTime)
                    )
                  )
                  .asMinutes()
              ),
          allDay: !!scheduleEvent.allDay
        },
        reason: scheduleEvent.reason,
        scheduleZoomMeeting: !!scheduleEvent.zoomMeetingUrl,
        scheduleTeamsMeeting: !!scheduleEvent.teamsMeetingUrl,
        scheduleGoogleMeetMeeting: !!scheduleEvent.googleMeetMeetingUrl,
        services: srv.map(service => ({
          value: service.service.id,
          label: `${
            service.service.billingCodes[0]
              ? service.service.billingCodes[0].cptCode + " - "
              : ""
          } ${service.service.name}`,
          data: service,
          costPerUnit: service.costPerUnit,
          numberOfUnits: service.numberOfUnits,
          billingCodes: service.scheduleEventServiceBillingCodes.map(bc => ({
            ...bc,
            numberOfUnits: bc.numberOfUnits
              ? bc.numberOfUnits
              : service.numberOfUnits
          }))
        })),
        icdCodeIds: scheduleEvent.episodesOfCare[0].icdCodes.map(icdCode => ({
          value: icdCode.id,
          label: `${icdCode ? icdCode.code : ""} - ${icdCode.description}`,
          data: icdCode
        })),
        zoomMeetingUrl: scheduleEvent.zoomMeetingUrl,
        isRecurrent: !!scheduleEvent.recurrence,
        recurrence: scheduleEvent.recurrence
          ? {
              id: scheduleEvent.recurrence?.id,
              frequencyNumber: scheduleEvent.recurrence?.frequencyNumber,
              frequencyUnit: scheduleEvent.recurrence?.frequencyUnit,
              endType: scheduleEvent.recurrence?.endType,
              endDate: scheduleEvent.recurrence?.endDate
                ? this.formatDateOnlyMysql(scheduleEvent.recurrence.endDate)
                : "",
              occurrences: scheduleEvent.recurrence?.occurrences,
              monthlyRepeat: scheduleEvent.recurrence?.monthlyRepeat,
              sunday: scheduleEvent.recurrence?.sunday,
              monday: scheduleEvent.recurrence?.monday,
              tuesday: scheduleEvent.recurrence?.tuesday,
              wednesday: scheduleEvent.recurrence?.wednesday,
              thursday: scheduleEvent.recurrence?.thursday,
              friday: scheduleEvent.recurrence?.friday,
              saturday: scheduleEvent.recurrence?.saturday
            }
          : {
              frequencyNumber: 1,
              frequencyUnit: "Days",
              monthlyRepeat: null,
              endType: "ByOccurrences",
              endDate: null,
              occurrences: 10,
              sunday: false,
              monday: false,
              tuesday: false,
              wednesday: false,
              thursday: false,
              friday: false,
              saturday: false
            },
        placeOfService: scheduleEvent.placeOfService
          ? {
              value: scheduleEvent.placeOfService.id,
              label: `${scheduleEvent.placeOfService.code} - ${scheduleEvent.placeOfService.name}`,
              data: scheduleEvent.placeOfService
            }
          : null,
        room: scheduleEvent.room
          ? {
              value: scheduleEvent.room.id,
              label: scheduleEvent.room.name,
              data: scheduleEvent.room
            }
          : null
      };
    } else if (this.scheduleEventDefaultData) {
      const scheduleEvent = this.scheduleEventDefaultData;
      currentUserProvider = this.users.find(
        user => user.value === this.scheduleEventDefaultData.providerId
      )?.data;

      if (scheduleEvent.billingInfo) {
        const billType = scheduleEvent.billingInfo.insuranceType;
        scheduleEvent.episodesOfCare[0].episodeOfCare.patient[
          "billType"
        ] = billType;

        if (billType === 1) {
          const insurances = Object.entries(scheduleEvent.episodesOfCare[0])
            .filter(
              ([key]) =>
                key === "primaryPatientInsurance" ||
                key === "secondaryPatientInsurance" ||
                key === "tertiaryPatientInsurance"
            )
            .map(([, value]) => value);

          scheduleEvent.episodesOfCare[0].episodeOfCare.patient[
            "insurances"
          ] = insurances;
        }
      }

      form = {
        eoc: scheduleEvent.episodesOfCare
          ? {
              value: scheduleEvent.episodesOfCare[0].episodeOfCare.id,
              label: this.formatEocObj(
                scheduleEvent.episodesOfCare[0].episodeOfCare
              ),
              data: scheduleEvent.episodesOfCare[0].episodeOfCare
            }
          : null,
        user: currentUserProvider
          ? {
              value: currentUserProvider.id,
              label: this.formatUser(currentUserProvider),
              data: currentUserProvider
            }
          : scheduleEvent.users && scheduleEvent.users.length
          ? {
              value: scheduleEvent.users[0].id,
              label: this.formatUser(scheduleEvent.users[0]),
              data: scheduleEvent.users[0]
            }
          : null,
        status: scheduleEvent.episodesOfCare
          ? scheduleEvent.episodesOfCare[0].scheduleEventStatus.id
          : defaultStatus.value,
        dateTimeDuration: {
          date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
          time: scheduleEvent.allDay
            ? "00:00:00"
            : this.formatTimeOnlyMysql(scheduleEvent.startDateTime),
          duration: scheduleEvent.allDay
            ? null
            : moment
                .duration(
                  moment(scheduleEvent.endDateTime).diff(
                    moment(scheduleEvent.startDateTime)
                  )
                )
                .asMinutes(),
          allDay: !!scheduleEvent.allDay
        },
        reason: scheduleEvent.reason,
        scheduleZoomMeeting: !!scheduleEvent.zoomMeetingUrl,
        scheduleTeamsMeeting: !!scheduleEvent.teamsMeetingUrl,
        scheduleGoogleMeetMeeting: !!scheduleEvent.googleMeetMeetingUrl,
        services: scheduleEvent.services
          ? scheduleEvent.services.map(service => ({
              value: service.service.id,
              label: `${
                service.service.billingCodes &&
                service.service.billingCodes.length > 0 &&
                service.service.billingCodes[0].cptCode
                  ? service.service.billingCodes[0].cptCode
                  : ""
              } - ${service.service.name ? service.service.name : ""}`,
              data: service,
              costPerUnit: service.costPerUnit,
              numberOfUnits: service.numberOfUnits,
              billingCodes: [
                {
                  costPerUnit: service.costPerUnit,
                  numberOfUnits: service.numberOfUnits
                }
              ]
            }))
          : [],
        icdCodeIds:
          scheduleEvent.episodesOfCare &&
          scheduleEvent.episodesOfCare[0].icdCodes
            ? scheduleEvent.episodesOfCare[0].icdCodes.map(icdCode => ({
                value: icdCode.id,
                label: `${icdCode ? icdCode.code : ""} - ${
                  icdCode.description
                }`,
                data: icdCode
              }))
            : [],
        zoomMeetingUrl: scheduleEvent.zoomMeetingUrl,
        teamsMeetingUrl: scheduleEvent.teamsMeetingUrl,
        googleMeetMeetingUrl: scheduleEvent.googleMeetMeetingUrl,
        isRecurrent: false,
        recurrence: {
          frequencyNumber: 1,
          frequencyUnit: "Days",
          monthlyRepeat: "ByDayOfMonth",
          endType: "ByOccurrences",
          endDate: null,
          occurrences: 10,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false
        },
        placeOfService: scheduleEvent.placeOfService
          ? {
              value: scheduleEvent.placeOfService.id,
              label: `${scheduleEvent.placeOfService.code} - ${scheduleEvent.placeOfService.name}`,
              data: scheduleEvent.placeOfService
            }
          : this.placesOfService.find(
              s => s.data.code == this.defaultPlaceOfServiceCode
            )
      };
    } else {
      form.status = this.statuses.find(s => s.data.code == "show").value;
      form.placeOfService = this.placesOfService.find(
        s => s.data.code == this.defaultPlaceOfServiceCode
      );
      form.dateTimeDuration = {
        date: null,
        time: null,
        duration: 60,
        allDay: false
      };
      form.user = this.users.find(u => u.value == this.providerId);
    }

    if (form.eoc) {
      this.eocDiagnosisCodes = await diagnosisCodeServer.query(
        false,
        form.eoc.value
      );
    }
    this.form = { ...form };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchScheduleEventStatuses",
      "fetchPlacesOfService"
    ]),
    setIsSelfPay(val) {
      this.isSelfPay = val;
      this.isLoading = false;
    },
    async addPatientInsurancesData(data) {
      this.form.eoc.primaryPatientInsuranceId = data.insurancePrimary.value;
      this.form.eoc.secondaryPatientInsuranceId = data.insuranceSecundary.value;
      this.form.eoc.tertiaryPatientInsuranceId = data.insuranceTertiary.value;
      this.form.eoc.billTypes = data.billTypes;
      if (this.form.services && this.form.services.length > 0) {
        await this.getServicesCostVariations(true);
      }
    },
    validateScheduleEventSubmit() {
      let withoutErrors = true;
      this.formErrors.eoc = [];
      if (!validateRequired(this.form.eoc)) {
        this.formErrors.eoc.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.status = [];
      if (!validateRequired(this.form.status)) {
        this.formErrors.status.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.user = [];
      if (this.requireProviderInEvents) {
        if (!validateRequired(this.form.user)) {
          this.formErrors.user.push("This field is required");
          withoutErrors = false;
        }
      }

      this.formErrors.placeOfService = [];
      if (!validateRequired(this.form.placeOfService)) {
        this.formErrors.placeOfService.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.services = [];
      if (this.form.services.length == 0) {
        this.formErrors.services.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.dateTimeDuration = [];
      if (
        !validateRequired(this.form.dateTimeDuration.date) ||
        (!this.form.dateTimeDuration.allDay &&
          (!validateRequired(this.form.dateTimeDuration.time) ||
            !validateRequired(this.form.dateTimeDuration.duration)))
      ) {
        this.formErrors.dateTimeDuration.push("This field is required");
        withoutErrors = false;
      } else if (!validateDate(this.form.dateTimeDuration.date)) {
        this.formErrors.dateTimeDuration.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(
          null,
          new Date(this.form.dateTimeDuration.date + " 00:00:00")
        ) ||
        (!this.form.dateTimeDuration.allDay &&
          ((this.selectedLocation &&
            this.form.dateTimeDuration.time <
              this.selectedLocation.operationStarts) ||
            (this.selectedLocation &&
              this.form.dateTimeDuration.time >
                this.selectedLocation.operationEnds) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time <
                this.location.operationStarts) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time > this.location.operationEnds)))
      ) {
        this.formErrors.dateTimeDuration.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      } else if (
        !this.form.dateTimeDuration.allDay &&
        (!validateNumberNotNegative(this.form.dateTimeDuration.duration) ||
          !validateNumberPositive(this.form.dateTimeDuration.duration))
      ) {
        this.formErrors.dateTimeDuration.push(
          "Duration cannot be a negative number!"
        );
        withoutErrors = false;
      } else if (
        !this.form.dateTimeDuration.allDay &&
        !validateNumberLessEqualThan(this.form.dateTimeDuration.duration, 1440)
      ) {
        this.formErrors.dateTimeDuration.push("Maximum value of 1440 minutes");
        withoutErrors = false;
      }

      this.formErrors.recurrence.ends = [];
      if (
        this.form.recurrence.endType == "ByDate" &&
        !validateRequired(this.form.recurrence.endDate)
      ) {
        this.formErrors.recurrence.ends.push(
          "You must specify a date to end the recurrence"
        );
        withoutErrors = false;
      } else if (
        this.form.recurrence.endType == "ByDate" &&
        this.form.recurrence.endDate < this.form.dateTimeDuration.date
      ) {
        this.formErrors.recurrence.ends.push(
          "Recurrence end date must be greater than start date"
        );
        withoutErrors = false;
      }

      for (let i = 0; i < this.form.services.length; i++) {
        const service = this.form.services[i];
        for (let k = 0; k < service.billingCodes.length; k++) {
          if (
            !validateNumberNotNegative(service.billingCodes[k].costPerUnit) ||
            service.billingCodes[k].numberOfUnits < 0
          ) {
            this.formErrors.services.push(
              "This field must have a positive value"
            );
            withoutErrors = false;
          }
        }
      }
      if (
        this.form.user &&
        this.form.services &&
        this.form.services.length > 0 &&
        this.form.user.data &&
        this.form.user.data.services &&
        this.form.user.data.services.length < this.servicesAll.length
      ) {
        let userServicesId = this.usersAll.find(
          user => user.value == this.form.user.value
        ).data.services;
        let servicesNoProviders = this.form.services.filter(
          s => !userServicesId.includes(s.value)
        );
        if (servicesNoProviders.length > 0) {
          this.formErrors.services.push(
            "Selected provider does not provide services: " +
              servicesNoProviders.map(s => s.label).join(", ")
          );
          withoutErrors = false;
        }
      }

      return withoutErrors;
    },
    async submitScheduleEvent(
      modalEvent,
      modificationType = "ModifySingleEvent"
    ) {
      //modalEvent.preventDefault();
      let checkData = {
        users: this.form.user ? [this.form.user.value] : [],
        episodesOfCare: this.form.eoc ? [this.form.eoc.data] : [],
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime
      };
      if (this.scheduleEventId) checkData.id = this.scheduleEventId;

      const result = await this.validateScheduleEventSubmit();
      const noDoubleBooking = await this.checkForDoubleBookedEvent(checkData);
      const noDoubleBookingEoc = await this.checkForDoubleBookedEventEoc(checkData);
      let doubleBookingAlert = {
        data: {
          value: true
        }
      };

      if (result && noDoubleBooking?.doubleBooked && noDoubleBookingEoc?.doubleBookedEoc) {
        try {
          doubleBookingAlert = await this.$dialog.confirm(
            {
              title: "This provider and this patient already have an appointment at this time."
            },
            {
              view: "multiple-actions",
              cancelText: "Keep editing",
              options: [{ value: true, label: "Save" }]
            }
          );
        } catch (error) {
          return;
        }
      }
      else if (result && noDoubleBooking?.doubleBooked) {
        try {
          doubleBookingAlert = await this.$dialog.confirm(
            {
              title: "This provider already has an appointment at this time."
            },
            {
              view: "multiple-actions",
              cancelText: "Keep editing",
              options: [{ value: true, label: "Save" }]
            }
          );
        } catch (error) {
          return;
        }
      }
      else if (result && noDoubleBookingEoc?.doubleBookedEoc) {
        try {
          doubleBookingAlert = await this.$dialog.confirm(
            {
              title: "This patient already has an appointment at this time."
            },
            {
              view: "multiple-actions",
              cancelText: "Keep editing",
              options: [{ value: true, label: "Save" }]
            }
          );
        } catch (error) {
          return;
        }
      }

      if (result && doubleBookingAlert.data.value === true) {
        const groupedServices = this.groupServices();
        let response = {};
        const formData = {
          type: "Appointment",
          episodesOfCare: [
            {
              ...(this.form.participantId
                ? { id: this.form.participantId }
                : {}),
              episodeOfCareId: this.form.eoc.value,
              icdCodeIds: this.form.icdCodeIds.map(icdCode => icdCode.value),
              statusId: this.form.status,
              primaryPatientInsuranceId: this.form.eoc
                .primaryPatientInsuranceId,
              secondaryPatientInsuranceId: this.form.eoc
                .secondaryPatientInsuranceId,
              tertiaryPatientInsuranceId: this.form.eoc
                .tertiaryPatientInsuranceId,
              billTypes: this.form.eoc.billTypes ? 0 : 1
            }
          ],
          users: this.form.user ? [this.form.user.value] : [],
          allDay: this.form.dateTimeDuration.allDay,
          startDateTime: this.startDateTime,
          endDateTime: this.endDateTime,
          services: groupedServices.map(service => ({
            serviceId: service.serviceId,
            costPerUnit:
              service.billingCodes.length &&
              service.billingCodes[0]?.costPerUnit
                ? parseFloat(service.billingCodes[0].costPerUnit)
                : 0,
            numberOfUnits:
              service.billingCodes.length &&
              service.billingCodes[0]?.numberOfUnits
                ? parseFloat(service.billingCodes[0]?.numberOfUnits)
                : 0,
            billingCodes: service.billingCodes.map(bc => ({
              ...bc,
              costPerUnit: bc.costPerUnit != "" || null ? bc.costPerUnit : 0,
              numberOfUnits: bc.numberOfUnits,
              scheduleEventId: this.scheduleEventId,
              serviceId: service.serviceId
            })),
            color: service.color != null ? service.color : null
          })),
          reason: this.form.reason,
          scheduleZoomMeeting: this.form.scheduleZoomMeeting,
          scheduleTeamsMeeting: this.form.scheduleTeamsMeeting,
          scheduleGoogleMeetMeeting: this.form.scheduleGoogleMeetMeeting,
          modification: modificationType,
          ...(this.form.isRecurrent && {
            recurrence: {
              frequencyNumber: this.form.recurrence.frequencyNumber,
              frequencyUnit: this.form.recurrence.frequencyUnit,
              endType: this.form.recurrence.endType,
              ...(this.form.recurrence.endType == "ByDate" && {
                endDate: this.formatEndDateSql(this.form.recurrence.endDate)
              }),
              ...(this.form.recurrence.endType == "ByOccurrences" && {
                occurrences: this.form.recurrence.occurrences
              }),
              ...(this.form.recurrence.frequencyUnit == "Months" && {
                monthlyRepeat: this.form.recurrence.monthlyRepeat
              }),
              ...(this.form.recurrence.frequencyUnit == "Weeks" && {
                sunday: this.form.recurrence.sunday,
                monday: this.form.recurrence.monday,
                tuesday: this.form.recurrence.tuesday,
                wednesday: this.form.recurrence.wednesday,
                thursday: this.form.recurrence.thursday,
                friday: this.form.recurrence.friday,
                saturday: this.form.recurrence.saturday
              })
            }
          }),
          placeOfServiceId: this.form.placeOfService
            ? this.form.placeOfService.value
            : null,
          roomId: this.form.room ? this.form.room.value : null,
          locationId: this.location?.id
        };
        if (this.scheduleEventId) {
          try {
            const dialog = await this.$dialog.confirm(
              {
                title: "Do you want to notify participants?"
              },
              {
                view: "multiple-actions",
                options: [
                  { value: true, label: "Yes" },
                  { value: false, label: "No" }
                ]
              }
            );
            formData.resend = dialog.data.value;

            if (formData.recurrence && formData.recurrence.endDate)
              formData.recurrence.endDate = formData.recurrence.endDate.replace(
                " ",
                "T"
              );

            response = await scheduleEventServer.update(
              this.scheduleEventId,
              formData
            );
          } catch (error) {
            if (error.response.data.date) {
              this.errorMessage = `${
                error.response.data.message
              } ${formatDatePretty(error.response.data.date)}`;
            } else {
              this.errorMessage =
                error.response.data.errorCode === "tooMuchOcurrences"
                  ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                  : error.response.data.message;
            }
            return;
          }
        } else {
          try {
            response = await scheduleEventServer.create(formData);
          } catch (error) {
            if (error.response.data.date) {
              this.errorMessage = `${
                error.response.data.message
              } ${formatDatePretty(error.response.data.date)}`;
            } else {
              this.errorMessage =
                error.response.data.errorCode === "tooMuchOcurrences"
                  ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                  : error.response.data.message;
            }
            return;
          }
        }

        if (formData.scheduleZoomMeeting && !response.zoomMeetingUrl) {
          this.$dialog.alert(
            {
              title: `The event was ${
                this.scheduleEventId ? `updated` : `created`
              } successfully. The user doesn't have an active account on Zoom. For this reason, the zoom meeting was not attached.`
            },
            {
              view: "info"
            }
          );
        }
        if (formData.scheduleTeamsMeeting && !response.teamsMeetingUrl) {
          this.$dialog.alert(
            {
              title: `The event was ${
                this.scheduleEventId ? `updated` : `created`
              } successfully, but the teams meeting was not attached.`
            },
            {
              view: "info"
            }
          );
        }
        if (formData.scheduleGoogleMeetMeeting && !response.googleMeetMeetingUrl) {
          this.$dialog.alert(
            {
              title: `The event was ${
                this.scheduleEventId ? `updated` : `created`
              } successfully, but the google meet meeting was not attached.`
            },
            {
              view: "info"
            }
          );
        }
        this.showAlertSuccess();
        this.$emit("onModify");
      } else {
        //this.$dialog.alert("You have some errors in the form");
      }
    },
    async showRecurringEventModal() {
      this.$refs["form-modal"].show();
    },
    async fetchEocs() {
      const optionsResult = await eocServer.queryList(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locations: this.locationId ? [this.locationId] : [],
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.episodesOfCare = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.episodesOfCare = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          if (search.length >= 3) {
            loading(true);
          }

          this.searchingEocs = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              orderBy: "status",
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.episodesOfCare = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: search,
            data: eoc
          }));
          this.searchingEocs = false;
          if (search.length >= 3) {
            loading(false);
          }
        }
      }, 1000);
    },
    async fetchUsers() {
      const users = await userServer.getUserServices();
      this.usersAll = users
        .filter(user => user.providerInfo)
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }));
      this.users = this.usersAll;
    },
    async fetchRooms() {
      const rooms = await roomServer.query();
      this.rooms = rooms.map(room => ({
        value: room.id,
        label: room.name,
        data: room
      }));
    },
    async fetchServices() {
      const services = await serviceServer.query({
        type: "Service"
      });
      this.servicesAll = services.map(service => ({
        value: service.id,
        label: `${
          service.billingCodes[0] ? service.billingCodes[0].cptCode + " - " : ""
        } ${service.name}`,
        data: service,
        costPerUnit: service.costPerUnit,
        numberOfUnits: 1,
        billingCodes: service.billingCodes.map(bc => ({
          ...bc,
          mod1: bc.mod1 ? bc.mod1 : "",
          mod2: bc.mod2 ? bc.mod2 : "",
          mod3: bc.mod3 ? bc.mod3 : "",
          mod4: bc.mod4 ? bc.mod4 : ""
        }))
      }));
      this.defaultServicesAll = [...services];
      this.services = this.servicesAll;
    },
    splitServicesByBillingCodes(scheduleEvent) {
      let services = scheduleEvent.services ?? [];
      let result = [];

      services.forEach(service => {
        let billingCodes =
          service.service.scheduleEventServiceBillingCodes ?? [];

        if (billingCodes.length) {
          billingCodes.forEach(billingCode => {
            let byBillingCode = {
              ...service,
              scheduleEventServiceBillingCodes: [billingCode]
            };

            result.push(byBillingCode);
          });
        } else {
          result.push({
            ...service,
            scheduleEventServiceBillingCodes: []
          });
        }
      });

      return result;
    },
    groupServices() {
      const result = [];
      const groups = {};

      this.form.services.forEach(item => {
        const serviceId = item.value;
        if (!groups[serviceId]) {
          groups[serviceId] = [];
          result.push({ serviceId, services: groups[serviceId] });
        }
        groups[serviceId].push(item);
      });

      return result.map(item => ({
        ...item,
        data: item.services[0],
        billingCodes: [].concat(
          ...item.services.map(service => service.billingCodes)
        ),
        color: item.services[0].color
      }));
    },
    async fetchIcdCodeOptions(search, loading) {
      if (this.IcdCodesTimeout) {
        clearTimeout(this.IcdCodesTimeout);
        this.IcdCodesTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.icdCodes = [];
      }

      this.IcdCodesTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const icdCodes = await icdCodeServer.query(
            { searchPhrase: search },
            {
              "Hide-Loading": true
            }
          );
          this.icdCodes = icdCodes.map(icdCode => ({
            value: icdCode.id,
            label: `${icdCode ? icdCode.code : ""} - ${icdCode.description}`,
            data: icdCode
          }));
        } else this.icdCodes = [];
        loading(false);
      }, 1000);
    },
    getEocActiveIcdCodes() {
      const date = moment(
        this.form.dateTimeDuration.date,
        "YYYY-MM-DD"
      ).toDate();
      return this.eocDiagnosisCodes
        .filter(d => {
          const activeFrom =
            d.activeFrom && moment(d.activeFrom, "YYYY-MM-DD").toDate();
          const resolvedBy =
            d.resolvedBy && moment(d.resolvedBy, "YYYY-MM-DD").toDate();
          return (
            (d.status.name == "Active" || d.status.name == "Resolved") &&
            (activeFrom == null || activeFrom <= date) &&
            (resolvedBy == null || date <= resolvedBy)
          );
        })
        .map(d => ({
          value: d.icdCode.id,
          label: `${d.icdCode ? d.icdCode.code : ""} - ${
            d.icdCode.description
          }`,
          data: d.icdCode
        }));
    },

    async checkForDoubleBookedEvent(data) {
      return await scheduleEventServer
        .checkForDoubleBookedEvent(data)
        .then(result => {
          return result;
        });
    },
    async checkForDoubleBookedEventEoc(data) {
      return await scheduleEventServer
        .checkForDoubleBookedEventEoc(data)
        .then(result => {
          return result;
        });
    },
    resetServiceToDefaultValues(serviceId) {
      const defaultService = this.defaultServicesAll.find(
        service => service.id === serviceId
      );
      const serviceIndex = this.form.services.findIndex(
        service => service.value === serviceId
      );
      const service = this.form.services[serviceIndex];
      service.billingCodes[0].mod1 = defaultService.billingCodes[0]?.mod1;
      service.billingCodes[0].mod2 = defaultService.billingCodes[0]?.mod2;
      service.billingCodes[0].mod3 = defaultService.billingCodes[0]?.mod3;
      service.billingCodes[0].mod4 = defaultService.billingCodes[0]?.mod4;
    },
    async getServicesCostVariations(hideLoading = false) {
      if (this.isLoading) return;
      const patientData = this.form.eoc?.data;
      const queryVariations = patientData != undefined && {
        serviceIds: this.form.services.map(service => service.value),
        scheduleEventStatusId: this.form && this.form.status,
        payorIds: this.isSelfPay
          ? []
          : patientData.data &&
            patientData.data.patient &&
            patientData.data.patient.insurances &&
            patientData.data.patient.insurances.length
          ? [
              patientData.data.patient.insurances.find(
                x => x.id == this.form.eoc.primaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              patientData.data.patient.insurances.find(
                x => x.id == this.form.eoc.secondaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              patientData.data.patient.insurances.find(
                x => x.id == this.form.eoc.tertiaryPatientInsuranceId
              )?.filteredInsuranceCompanyId
            ]
          : patientData.patient &&
            patientData.patient.insurances &&
            patientData.patient.insurances.length
          ? [
              patientData.patient.insurances.find(
                x => x.id == this.form.eoc.primaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              patientData.patient.insurances.find(
                x => x.id == this.form.eoc.secondaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              patientData.patient.insurances.find(
                x => x.id == this.form.eoc.tertiaryPatientInsuranceId
              )?.filteredInsuranceCompanyId
            ]
          : [],
        credentialId: this.form.user && this.form.user.data.credential?.id,
        supervisorCredentialId:
          this.form.user && this.form.user.data.supervisorUser?.credential?.id,
        patientBillType: this.isSelfPay
          ? 0
          : this.form.eoc && (this.form.eoc.billTypes ? 0 : 1)
      };
      queryVariations.StartDateTime = new Date(
        `${this.form.dateTimeDuration.date} ${this.form.dateTimeDuration.time}`
      );
      if (patientData) {
        const extraHeaders = { "Hide-Loading": hideLoading };
        let variations = await serviceServer.getCostVariations(
          queryVariations,
          extraHeaders
        );
        variations = variations.filter(
          variation =>
            !this.form.services.some(service =>
              this.hasThisVariation(service, variation)
            )
        );

        this.form.services.forEach(service => {
          variations.forEach(variation => {
            if (service.value === variation.serviceId) {
              service.billingCodes[0].costPerUnit = variation.costPerUnit;
              service.billingCodes[0].mod1 = variation.mod1;
              service.billingCodes[0].mod2 = variation.mod2;
              service.billingCodes[0].mod3 = variation.mod3;
              service.billingCodes[0].mod4 = variation.mod4;
              service.variationId = variation.id;
            }
          });
        });
      }
    },
    hasThisVariation(service, variation) {
      return (
        service.variationId == variation.id &&
        service.billingCodes[0].mod1 == variation.mod1 &&
        service.billingCodes[0].mod2 == variation.mod2 &&
        service.billingCodes[0].mod3 == variation.mod3 &&
        service.billingCodes[0].mod4 == variation.mod4 &&
        service.billingCodes[0].costPerUnit == variation.costPerUnit
      );
    }
  },
  computed: {
    ...mapGetters("layout", [
      "datesNonWorking",
      "getCurrentAdjustedDateTime",
      "hasCurrentUserPermissionAccess"
    ]),
    ...mapState("client", {
      statuses: state =>
        state.scheduleEventStatuses.map(status => ({
          value: status.id,
          text: status.name,
          data: status
        })),
      placesOfService: state =>
        state.placesOfService.map(placeOfService => ({
          value: placeOfService.id,
          label: `${placeOfService.code}-${placeOfService.name}`,
          data: placeOfService
        }))
    }),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      isPracticeSuiteActive: state => state.isPracticeSuiteActive,
      isZoomEnabled: state => state.systemSetting.isZoomEnabled,
      isTeamsEnabled: state => state.systemSetting.isTeamsEnabled,
      isGoogleMeetEnabled: state => state.systemSetting.isGoogleMeetEnabled,
      teleHealtAutomaticModifier: state =>
        state.systemSetting.enableTelehealthSetMod,
      defaultPlaceOfServiceCode: state =>
        state.systemSetting.defaultPlaceOfServiceCode,
      requireProviderInEvents: state =>
        state.systemSetting.requireProviderInEvents
    }),
    ...mapGetters("client", ["selectedLocation"]),
    providerUsers() {
      return this.isPracticeSuiteActive
        ? this.users.filter(u => u.data.providerInfo)
        : this.users;
    },
    getPatientInsurancesActives() {
      const date = moment(
        this.form.dateTimeDuration.date,
        "YYYY-MM-DD"
      ).toDate();

      return this.form.eoc?.data?.patient?.insurances?.filter(pi => {
        const effectiveDate =
          pi.effectiveDate && moment(pi.effectiveDate, "YYYY-MM-DD").toDate();
        const terminationDate =
          pi.terminationDate &&
          moment(pi.terminationDate, "YYYY-MM-DD").toDate();
        return (
          (effectiveDate == null || effectiveDate <= date) &&
          (terminationDate == null || date <= terminationDate)
        );
      });
    },
    startDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time &&
        !this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = `${this.form.dateTimeDuration.date} ${
        this.form.dateTimeDuration.allDay
          ? "00:00:00"
          : this.form.dateTimeDuration.time
      }`;

      return this.formatDateTimeSql(dateTime);
    },
    endDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time ||
        this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = moment(this.startDateTime).add(
        this.form.dateTimeDuration.duration,
        "minutes"
      );

      return this.formatDateTimeSql(dateTime);
    },
    belongsToRecurrence() {
      return !!this.form.recurrence?.id;
    },
    formTitle() {
      if (this.scheduleEventId) {
        return this.belongsToRecurrence
          ? "Edit recurrent appointment"
          : "Edit appointment";
      } else {
        return "New appointment";
      }
    },
    eventIsInPast() {
      return (
        !this.startDateTime ||
        this.formatDateTimeMysql(this.startDateTime) <=
          this.formatDateTimeMysql(this.getCurrentAdjustedDateTime())
      );
    },
    statusLabelColor() {
      if (this.statuses.length == 0 || !this.form.status) {
        return "";
      }
      return this.statuses.find(u => u.value == this.form.status).data.color;
    },
    statusBackgroundColor() {
      if (this.statuses.length == 0 || !this.form.status) {
        return "";
      }
      return lightenDarkenColor(
        this.statuses.find(u => u.value == this.form.status).data.color,
        85
      );
    },
    selectedEoc: {
      get() {
        return this.form.eoc;
      },
      async set(newValue) {
        if (newValue) {
          this.eocDiagnosisCodes = await diagnosisCodeServer.query(
            false,
            newValue.value
          );
          const newIcdCodes = this.getEocActiveIcdCodes();
          this.form.icdCodeIds = newIcdCodes;

          this.prepopulatedIcdCodes = newIcdCodes;
        }
        this.form.eoc = newValue;
      }
    },
    selectedDateTimeDuration: {
      get() {
        return this.form.dateTimeDuration;
      },
      async set(newValue) {
        if (
          newValue?.date &&
          newValue.date != this.form.dateTimeDuration.date
        ) {
          if (
            newValue.date != this.form.dateTimeDuration.date &&
            this.eocDiagnosisCodes.length > 0
          ) {
            this.form.dateTimeDuration = newValue;
            const newIcdCodes = this.getEocActiveIcdCodes();
            this.form.icdCodeIds = newIcdCodes;

            this.prepopulatedIcdCodes = newIcdCodes;
          }
        }
        this.form.dateTimeDuration = newValue;
      }
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    applyTeleHealtAutomaticModifier() {
      return (
        this.teleHealtAutomaticModifier &&
        this.form.placeOfService &&
        this.form.placeOfService.data.code === "02"
      );
    }
  },
  watch: {
    "form.icdCodeIds": async function(value) {
      if (value && value.length > 0) {
        this.form.icdCodeIds.forEach(item => {
          const diagnosisCodeElement = this.eocDiagnosisCodes.find(
            x => x.icdCode.id == item.value
          );
          if (
            diagnosisCodeElement != null &&
            item.label.substring(0, 5) != "<span"
          ) {
            const newLabel = diagnosisCodeElement.category
              ? `<span class="badge text-white mr-1 p-1 badge-primary">${diagnosisCodeElement.category[0]}</span> ${item.label}`
              : item.label;
            item.label = newLabel;
            item.category = diagnosisCodeElement.category;
            this.form.icdCodeIds.sort((a, b) => {
              return a.category < b.category
                ? -1
                : a.category > b.category
                ? 1
                : 0;
            });
          }
        });
      }
    },
    "form.status": async function(newValue, oldValue) {
      if (
        JSON.stringify(newValue) !== JSON.stringify(oldValue) &&
        this.form.services.length > 0
      ) {
        await this.getServicesCostVariations();
      }
      const status = this.statuses.find(s => s.value == this.form.status);
      if (
        oldValue != null &&
        status.text == "Show" &&
        this.startDateTime != null &&
        this.endDateTime != null &&
        this.form.services.length > 0
      ) {
        let serviceIds = [];
        for (let service of this.form.services) {
          serviceIds.push(service.value);
        }

        let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
          this.startDateTime,
          this.endDateTime,
          status.text,
          serviceIds
        );

        if (Object.keys(servicePerUnit).length > 0) {
          this.form.services.forEach(service => {
            service.billingCodes.forEach(bc => {
              bc.numberOfUnits = servicePerUnit[service.value];
            });
          });
        }
      }
    },
    "form.eoc": {
      handler: async function(newValue, oldValue) {
        if (this.eocInsurances?.id != newValue?.value) {
          this.eocInsurances = null;
        }
        if (
          JSON.stringify(newValue) !== JSON.stringify(oldValue) &&
          this.form.services.length > 0
        ) {
          await this.getServicesCostVariations();
        }
      },
      deep: true
    },
    serviceCostVariations: {
      handler: function(newVariations, oldVariations) {
        oldVariations.forEach(oldVariation => {
          const newVariation = newVariations.find(
            variation => variation.serviceId === oldVariation.serviceId
          );
          if (!newVariation) {
            this.form.services.forEach(service => {
              if (service.value === oldVariation.serviceId) {
                let defaultService = this.servicesAll.find(
                  service => service.value === oldVariation.serviceId
                );
                service.billingCodes[0].costPerUnit =
                  defaultService.costPerUnit;
                service.billingCodes[0].mod1 =
                  defaultService.billingCodes[0].mod1;
                service.billingCodes[0].mod2 =
                  defaultService.billingCodes[0].mod2;
                service.billingCodes[0].mod3 =
                  defaultService.billingCodes[0].mod3;
                service.billingCodes[0].mod4 =
                  defaultService.billingCodes[0].mod4;
              }
            });
          }
        });
      },
      deep: true
    },
    applyTeleHealtAutomaticModifier: function(newVal) {
      if (newVal === true) {
        if (this.form.services.length > 0) {
          this.form.services.forEach(service => {
            if (service.billingCodes.length > 0) {
              service.billingCodes.forEach(bc => {
                if (bc.mod1 === "") {
                  bc.mod1 = "95";
                }
              });
            }
          });
        }
        if (this.servicesAll.length > 0) {
          this.servicesAll.forEach(service => {
            if (service.billingCodes && service.billingCodes.length > 0) {
              service.billingCodes.forEach(bc => {
                bc.mod1 === "" ? (bc.mod1 = "95") : null;
              });
            } else {
              this.$set(service, "billingCodes", [
                {
                  ...service.billingCodes,
                  mod1: "95",
                  costPerUnit: 0,
                  numberOfUnits: 1
                }
              ]);
            }
          });
        }
      }
    },
    "form.user": {
      handler: async function(value) {
        if (value) {
          const currentUserServices = this.users.find(
            user => user.value == value.value
          );
          if (
            currentUserServices.data.services.length == this.servicesAll.length
          ) {
            this.services = this.servicesAll;
          } else {
            this.services = this.servicesAll.filter(s =>
              currentUserServices.data.services?.includes(s.value)
            );
          }
          this.noCached = true;
        } else {
          this.services = this.servicesAll;
          this.noCached = false;
          if (this.form.services.length > 0) {
            this.formErrors.services = [];
          }
        }
        await this.getServicesCostVariations();
      },
      deep: true
    },
    "form.services": {
      handler: async function(value, oldValue) {
        if (value && value.length > 0) {
          let servicesId = this.form.services.map(s => s.value);
          this.users = this.usersAll.filter(u =>
            servicesId.every(
              s =>
                u.data.services?.includes(s) ||
                u.data.services?.length == this.servicesAll.length
            )
          );
        } else {
          this.users = this.usersAll;
        }
        if (this.form && this.form.eoc) {
          if (
            JSON.stringify(value) !== JSON.stringify(oldValue) &&
            this.form.services.length > 0
          ) {
            await this.getServicesCostVariations();
          }
        }
        const status = this.statuses.find(s => s.value == this.form.status);
        if (
          value.length > oldValue.length &&
          this.startDateTime != null &&
          this.endDateTime != null
        ) {
          let serviceIds = [];
          for (let service of value) {
            serviceIds.push(service.value);
          }
          let serviceIdsOld = [];
          for (let service of oldValue) {
            serviceIdsOld.push(service.value);
          }
          let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
            this.startDateTime,
            this.endDateTime,
            status.text,
            serviceIds
          );

          if (Object.keys(servicePerUnit).length > 0) {
            value.forEach(service => {
              let numberOfUnits = servicePerUnit[service.value];
              service.billingCodes.forEach(bc => {
                bc.numberOfUnits =
                  this.scheduleEventId != null &&
                  serviceIdsOld.length == 0 &&
                  !this.servicesEmpty
                    ? bc.numberOfUnits
                    : this.scheduleEventId != null &&
                      !this.servicesEmpty &&
                      serviceIdsOld.includes(service.value)
                    ? bc.numberOfUnits
                    : numberOfUnits;
              });
            });
          }
        }
        if (oldValue.length > value.length && value.length == 0) {
          this.servicesEmpty = true;
        } else {
          this.servicesEmpty = false;
        }
      },
      deep: true
    },
    "form.dateTimeDuration": {
      handler: async function(newValue, oldValue) {
        if (this.lastTimeout) {
          clearTimeout(this.lastTimeout);
          this.lastTimeout = null;
        }
        this.lastTimeout = setTimeout(async () => {
          const status = this.statuses.find(s => s.value == this.form.status);
          if (
            newValue.date != null &&
            newValue.time != null &&
            !newValue.allDay &&
            (this.scheduleEventId == null ||
              (oldValue != null && oldValue.duration != newValue.duration)) &&
            this.form.services.length > 0
          ) {
            let serviceIds = [];
            for (let service of this.form.services) {
              serviceIds.push(service.value);
            }

            let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
              this.startDateTime,
              this.endDateTime,
              status.text,
              serviceIds
            );

            if (Object.keys(servicePerUnit).length > 0) {
              this.form.services.forEach(service => {
                service.billingCodes.forEach(bc => {
                  bc.numberOfUnits = servicePerUnit[service.value];
                });
              });
            }
          }
        }, 1500);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
::v-deep .vs__dropdown-menu {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  li {
    white-space: normal;
  }
}
</style>
